import { IState, IKeyValue } from '@fareye/types'
import {
  Button,
  Card,
  Flex,
  Grid,
  Input,
  SearchSelect,
  Span,
  toast
} from '@fareye/ui'
import {
  cancelToken,
  cloneDeep,
  FaPlusCircle,
  FaTrash,
  get,
  isEmpty,
  put,
  post,
  snakeCase
} from '@fareye/utils'
import { ErrorMessage, FieldArray, Form, Formik } from 'formik'
import React from 'react'
import { mixed, object, string, array } from 'yup'
import { InputLabel } from '../../../../style/styled'
import {
  stringArrayToLabelValueArray,
  getErrorMessage
} from '../../../../utility/funcs'
import { IApiConfig } from '../../../../utility/types'
import { Header } from '../../components/Header'
import { MultiAttribute } from './MultiAttribute'
import { HEADER_INFLECTION } from '../../../../config/constants'
import { CustomErrorMessage } from '../../components/ErroMessage'
import { useTranslation } from '@fareye/common'

interface IFilterForm {
  apiConfig: IApiConfig
  selectedDataForEdit: any
  mapperData: IState<string> | Object
  fetchData(): Promise<void>
}
export interface IFormState {
  name: string
  configType: any
  config: Array<{ attribute: any; value: any }>
  operator: any
}
const OPERATORS = [
  { label: 'AND', value: 'AND' },
  { label: 'OR', value: 'OR' }
]
export function FilterForm({
  apiConfig,
  selectedDataForEdit,
  mapperData,
  fetchData
}: IFilterForm) {
  const initialFormState = {
    name: '',
    configType: null,
    operator: OPERATORS[0],
    config: [
      {
        attribute: null,
        value: null
      }
    ]
  }
  const [initialState, setInitialState] = React.useState<IFormState>(
    initialFormState
  )
  const [formSubmitted, setFormSubmitted] = React.useState(false)
  const { t } = useTranslation()
  const [models, setModels] = React.useState<Array<any>>([])
  const [modelAndAttribute, setModelAndAttributes] = React.useState<
    IKeyValue<Array<string>>
  >({})
  const validation = object().shape({
    name: string().required(t('* Required')),
    configType: mixed().required(t('* Required')),
    operator: mixed().required(t('* Required')),
    config: array(
      object().shape({
        attribute: mixed().required(t('*Required')),
        value: mixed().required(t('*Required'))
      })
    ).min(1)
  })
  const source = cancelToken().source()
  const crudSource = cancelToken().source()
  React.useEffect(() => {
    fetchModelAndAttribute()
    return () => source.cancel('Unmount Fetch model call')
  }, [])

  React.useEffect(() => {
    if (!isEmpty(selectedDataForEdit)) {
      const model = (mapperData as any)[selectedDataForEdit.type]
      let payload: IFormState = {
        name: selectedDataForEdit.name,
        operator: {
          label: selectedDataForEdit.operator,
          value: selectedDataForEdit.operator
        },
        configType: {
          label: model,
          value: model
        },
        config: generateConfigForEdit(selectedDataForEdit.config)
      }
      setInitialState(payload)
    } else {
      setInitialState(initialFormState)
    }
  }, [selectedDataForEdit])

  function generateConfigForEdit(config: IKeyValue<any>) {
    if (config) {
      return Object.entries(config).map(([key, value]) => {
        return {
          attribute: {
            label: key,
            value: key
          },
          value: stringArrayToLabelValueArray(value)
        }
      })
    }
    return [{ attribute: null, value: null }]
  }

  async function fetchModelAndAttribute() {
    try {
      const { getModels } = apiConfig
      const { data } = await get(getModels.apiUrl, source.token)
      const tempModel = stringArrayToLabelValueArray(Object.keys(data.response))
      let tempModelAndAttribute: any = {}
      Object.entries(data.response).forEach(([key, value]) => {
        tempModelAndAttribute[key] = stringArrayToLabelValueArray(
          value as string[]
        )
      })
      setModelAndAttributes(tempModelAndAttribute)
      setModels(tempModel)
    } catch (ex:any) {
      setModels([])
    }
  }

  const filteredAttribute = (config: Array<any>, model: string) => {
    const tempConfig = config
      .filter(ele => ele.attribute)
      .map(ele => ele.attribute.label)

    const temp = modelAndAttribute[model]
    if (temp) {
      return temp.filter((ele: any) => {
        return !tempConfig.includes(ele.label)
      })
    }
    return []
  }

  function handleFormAction(values: any, resetForm: any) {
    const formValue = filterFormValues(values)
    const payload = {
      configFilter: {
        ...formValue
      }
    }
    if (!isEmpty(selectedDataForEdit)) {
      updateFilter(payload, selectedDataForEdit.id)
    } else {
      createFilter(payload, resetForm)
    }
  }

  function filterFormValues(values: any) {
    const formValue = cloneDeep(values)
    formValue.configType = formValue.configType.label
    formValue.operator = formValue.operator.value
    formValue.config = formValue.config.reduce((obj: any, ele: any) => {
      const attr = (ele.attribute.label.includes('.') || ele.attribute.label.includes('key'))
        ? ele.attribute.label
        : snakeCase(ele.attribute.label)
      obj[attr] = ele.value.map((val: any) => val.label)
      return obj
    }, {})
    return formValue
  }

  async function createFilter(payload: any, resetForm: any) {
    try {
      setFormSubmitted(true)
      const resp = await post(
        `${apiConfig.addFilter.apiUrl}`,
        payload,
        crudSource.token,
        {
          ...HEADER_INFLECTION,
          validateStatus: status => {
            return true
          }
        }
      )
      if (resp.status > 300) {
        const error: any = new Error()
        const error_message = resp.data.error_message
          ? resp.data.error_message
          : resp.data.error
        if (error_message && resp.status != 500) {
          error.response = { data: { error: error_message } }
        }
        throw error
      }

      toast.success(t('Filter added successfully.'))
      resetForm()

      fetchData()
      setFormSubmitted(false)
    } catch (ex:any) {
      const errorMessage = getErrorMessage(ex)
      toast.error(errorMessage)
      setFormSubmitted(false)
    }
  }

  async function updateFilter(payload: any, id: number | string) {
    try {
      setFormSubmitted(true)
      const resp = await put(
        `${apiConfig.editFilter.apiUrl}/${id}`,
        payload,
        crudSource.token,
        {
          ...HEADER_INFLECTION,
          validateStatus: status => {
            return true
          }
        }
      )
      if (resp.status > 300) {
        const error: any = new Error()
        const error_message = resp.data.error_message
          ? resp.data.error_message
          : resp.data.error
        if (error_message && resp.status != 500) {
          error.response = { data: { error: error_message } }
        }
        throw error
      }
      toast.success(t('Filter updated successfully.'))
      fetchData()
      setFormSubmitted(false)
    } catch (ex:any) {
      const errorMessage = getErrorMessage(ex)
      toast.error(errorMessage)
      setFormSubmitted(false)
    }
  }

  return (
    <Formik
      initialValues={initialState}
      validationSchema={validation}
      isInitialValid={validation.isValidSync(initialState)}
      enableReinitialize={true}
      onSubmit={(values, { resetForm }) => {
        handleFormAction(values, resetForm)
      }}
    >
      {({
        values,
        handleChange,
        handleSubmit,
        handleReset,
        setFieldValue,
        setFieldTouched,
        isValid
      }) => (
        <Form style={{ width: '100%' }}>
          <Card header={<Header label="Filter Form" />} p={5} variant="m">
            <Flex p={20} column>
              <Grid gridTemplateColumns={'.5fr 1fr .5fr'} mb={4} gridGap="10px">
                <Flex column>
                  <InputLabel>{t('Name')}</InputLabel>
                  <Input
                    placeholder={t('Enter Filter name')}
                    onChange={handleChange}
                    name="name"
                    value={values.name}
                  />
                  <ErrorMessage component={CustomErrorMessage} name="name" />
                </Flex>
                <Flex column>
                  <InputLabel>{t('Type')}</InputLabel>
                  <SearchSelect
                    options={models}
                    value={values.configType}
                    onChange={(value: any) => {
                      setFieldValue('configType', value)
                      setFieldValue('config', [
                        {
                          attribute: null,
                          value: null
                        }
                      ])
                      // getAttribute(value.label)
                    }}
                    placeholder={t('Select')}
                    name="configType"
                    onBlur={() => {
                      setFieldTouched('configType', true)
                    }}
                  ></SearchSelect>
                  <ErrorMessage
                    component={CustomErrorMessage}
                    name="configType"
                  />
                </Flex>
                <Flex column>
                  <InputLabel>{t('Operator')}</InputLabel>
                  <SearchSelect
                    options={OPERATORS}
                    value={values.operator}
                    onChange={(value: any) => {
                      setFieldValue('operator', value)
                    }}
                    placeholder={t('Select')}
                    name="operator"
                    onBlur={() => {
                      setFieldTouched('operator', true)
                    }}
                  ></SearchSelect>
                  <ErrorMessage
                    component={CustomErrorMessage}
                    name="operator"
                  />
                </Flex>
              </Grid>
              <Grid mb={4}>
                {values.configType && (
                  <Flex column>
                    <FieldArray
                      name="config"
                      render={(arrayHelper: any) => (
                        <Card m={0} variant="xs">
                          <Flex column>
                            {values.config.map((ele, index) => {
                              return (
                                <Flex
                                  key={`filterForm${index}`}
                                  justifyContentSpaceBetween
                                  width={1}
                                  style={{ marginBottom: '4px' }}
                                  alignItemsFlexEnd
                                >
                                  <MultiAttribute
                                    apiConfig={apiConfig}
                                    model={values.configType.label}
                                    index={index}
                                    editFlag={
                                      !isEmpty(selectedDataForEdit)
                                        ? true
                                        : false
                                    }
                                    fieldValue={values.config}
                                    setFieldValue={setFieldValue}
                                    attribute={filteredAttribute(
                                      values.config,
                                      values.configType.label
                                    )}
                                  />

                                  {index > 0 && (
                                    <Span style={{ padding: '10px 15px' }}>
                                      <FaTrash
                                        onClick={() =>
                                          arrayHelper.remove(index)
                                        }
                                      />
                                    </Span>
                                  )}
                                </Flex>
                              )
                            })}
                            <Flex justifyContentFlexEnd>
                              <Button
                                onClick={() => {
                                  arrayHelper.push({})
                                }}
                                style={{ marginTop: '5px' }}
                                leftChild={
                                  <FaPlusCircle
                                    style={{
                                      marginRight: '7px'
                                    }}
                                  />
                                }
                                type="button"
                                variant="primary"
                              >
                                {t('Add Filter')}
                              </Button>
                            </Flex>
                          </Flex>
                        </Card>
                      )}
                    />
                    <ErrorMessage
                      component={CustomErrorMessage}
                      name="config"
                    />
                  </Flex>
                )}
              </Grid>
              {
                <Flex justifyContentFlexEnd>
                  <Button
                    variant="m primary"
                    onClick={handleSubmit}
                    type="submit"
                    mr={4}
                    isLoading={formSubmitted}
                  >
                    {!isEmpty(selectedDataForEdit) ? t('Update') : t('Submit')}
                  </Button>
                  <Button
                    variant="m danger"
                    onClick={handleReset}
                    type="button"
                  >
                    {t('Reset')}
                  </Button>
                </Flex>
              }
            </Flex>
          </Card>
        </Form>
      )}
    </Formik>
  )
}
